(function () {
  const COMPONENT_SELECTOR = 'product-recommendations';

  class ProductComplementaryManager {
    constructor(container) {
      this.container = container;
      this.products = Array.from(container.querySelectorAll('product-complementary'));
      this.limit = parseInt(container.dataset.limit || '5', 10);
      
      this.init();
    }

    init() {
      if (this.products.length === 0) {
        return;
      }

      this.manageProductVisibility();
    }

    /**
     * Check if a product has any available variants
     */
    hasAvailableVariants(productElement) {
      const form = productElement.querySelector('form.product-form');
      if (!form) return false;

      // Check for variant selector (multiple variants)
      const variantSelect = form.querySelector('select[name="id"]');
      if (variantSelect) {
        const options = Array.from(variantSelect.options);
        // Check if any option is enabled (not disabled)
        const hasAvailable = options.some(option => !option.disabled);
        return hasAvailable;
      }

      // Check for single variant (hidden input)
      const variantInput = form.querySelector('input[type="hidden"][name="id"]');
      if (variantInput) {
        const submitButton = form.querySelector('button[type="submit"]');
        // If button is not disabled, variant is available
        return submitButton && !submitButton.disabled;
      }

      return false;
    }

    /**
     * Manage visibility: hide out-of-stock, show alternatives
     */
    manageProductVisibility() {
      let visibleCount = 0;
      let hiddenWithStock = [];

      // First pass: identify products with stock and hide those without
      this.products.forEach((product, index) => {
        const hasStock = this.hasAvailableVariants(product);
        
        if (hasStock) {
          if (visibleCount < this.limit) {
            // Show this product (it has stock and we haven't hit limit)
            product.removeAttribute('hidden');
            product.style.display = '';
            visibleCount++;
          } else {
            // Has stock but we've hit limit - keep in reserve
            product.setAttribute('hidden', 'hidden');
            product.style.display = 'none';
            hiddenWithStock.push(product);
          }
        } else {
          // No stock - hide it
          product.setAttribute('hidden', 'hidden');
          product.style.display = 'none';
        }
      });

      // Second pass: if we haven't reached limit, show hidden products with stock
      if (visibleCount < this.limit && hiddenWithStock.length > 0) {
        const needed = this.limit - visibleCount;
        hiddenWithStock.slice(0, needed).forEach(product => {
          product.removeAttribute('hidden');
          product.style.display = '';
          visibleCount++;
        });
      }

      // Hide entire container if no products are visible
      if (visibleCount === 0) {
        this.container.setAttribute('hidden', 'hidden');
        this.container.style.display = 'none';
      } else {
        this.container.removeAttribute('hidden');
        this.container.style.display = '';
      }

      console.log('[ProductComplementary] Visibility managed:', {
        total: this.products.length,
        visible: visibleCount,
        limit: this.limit
      });
    }
  }

  /**
   * Multi-option picker: the first select leads, every following select only lists values that
   * exist together with the selects before it (sold-out ones stay visible but disabled). The
   * matching variant id goes into the form's hidden id input.
   */
  const initOptionPicker = (picker) => {
    if (picker.dataset.optionsInitialized === 'true') return;
    picker.dataset.optionsInitialized = 'true';

    const form = picker.closest('form');
    const idInput = form && form.querySelector('input[name="id"]');
    const jsonEl = picker.querySelector('script[type="application/json"]');
    if (!idInput || !jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    const soldOutLabel = picker.dataset.soldOutLabel || '';
    const submitButton = form.querySelector('[type="submit"]');
    const card = picker.closest('product-complementary');
    const image = card && card.querySelector('.horizontal-product__media img');
    const selects = Array.from(picker.querySelectorAll('select[data-option-position]'));
    const positions = selects.map((select) => parseInt(select.dataset.optionPosition, 10));
    const masters = selects.map((select) =>
      Array.from(select.options).map((option) => ({
        value: option.value,
        label: option.dataset.label || option.textContent.trim()
      }))
    );

    const matchesSelects = (variant, count) =>
      selects.slice(0, count).every((select, i) => variant.options[positions[i]] === select.value);

    const update = () => {
      for (let i = 1; i < selects.length; i++) {
        const select = selects[i];
        const current = select.value;
        const candidates = variants.filter((variant) => matchesSelects(variant, i));

        // Rebuild instead of hiding: Safari ignores hidden <option>s.
        select.replaceChildren();
        masters[i].forEach(({ value, label }) => {
          const withValue = candidates.filter((variant) => variant.options[positions[i]] === value);
          if (withValue.length === 0) return;
          const available = withValue.some((variant) => variant.available);
          const option = new Option(available ? label : `${label} – ${soldOutLabel}`, value);
          option.dataset.label = label;
          option.disabled = !available;
          select.add(option);
        });

        const options = Array.from(select.options);
        const next = options.find((o) => o.value === current && !o.disabled) || options.find((o) => !o.disabled) || options[0];
        if (next) select.value = next.value;
      }

      const variant = variants.find((v) => matchesSelects(v, selects.length));
      if (variant) idInput.value = variant.id;
      if (submitButton) submitButton.disabled = !(variant && variant.available);
      if (variant) showVariantImage(variant);
    };

    // Swap the card image to the variant's own image (e.g. the Buddy motif).
    const showVariantImage = (variant) => {
      const media = variant.featured_image;
      if (!image || !media || !media.src) return;
      const src = media.src.startsWith('//') ? `https:${media.src}` : media.src;
      if (image.dataset.variantSrc === src) return;
      image.dataset.variantSrc = src;
      const sized = (width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
      image.srcset = [165, 360, 533]
        .filter((width) => !media.width || media.width >= width)
        .map((width) => `${sized(width)} ${width}w`)
        .join(', ');
      image.src = sized(533);
      if (media.alt) image.alt = media.alt;
    };

    selects.forEach((select) => select.addEventListener('change', update));
    update();
  };

  const initComplementaryManager = (scope) => {
    const container = scope || document;
    if (container.querySelectorAll) {
      container.querySelectorAll('[data-complementary-options]').forEach(initOptionPicker);
    }
    const elements = container.querySelectorAll
      ? container.querySelectorAll(COMPONENT_SELECTOR)
      : [];

    elements.forEach((element) => {
      if (element.dataset.complementaryInitialized === 'true') {
        return;
      }
      new ProductComplementaryManager(element);
      element.dataset.complementaryInitialized = 'true';
    });
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initComplementaryManager(document));
  } else {
    initComplementaryManager(document);
  }

  // Re-initialize when cart drawer updates
  document.addEventListener('cart:updated', () => {
    setTimeout(() => initComplementaryManager(document), 100);
  });

  // Listen for Shopify section events
  document.addEventListener('shopify:section:load', (event) => {
    initComplementaryManager(event.target);
  });
})();

